import { useState, useEffect } from "react";
import { Link } from "react-router-dom";

export default function ProcessingNotesList({ helperData, setOpenModal }) {
  const [copied, setCopied] = useState("");

  const handleCloseModal = () => {
    setOpenModal("");
  }

  useEffect(() => {
    const handleKeyDown = (event) => {
      if (event.key === "Escape") {
        handleCloseModal();
      }
    };

    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, []);

  const copyToClipboard = async (e) => {
    navigator.clipboard.writeText(e.target.value);
    setCopied(`Successfully copied: ${e.target.value}`);
    setTimeout(() => {
      setCopied("");
    }, 1000)
  }

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 z-20 flex items-center justify-center">
      <div className="bg-white p-6 rounded-lg shadow-lg w-4/5 max-h-[80vh] overflow-y-auto">
        {/* Header */}
        <div className="flex justify-between items-center mb-4 border-b pb-2">
          <div>
            <h2 className="text-xl font-bold text-dark-purple">
              Processing Notes Dashboard
            </h2>
            <p className="text-gray-600 text-sm">
              {helperData ? `${helperData.length} notes currently being processed` : 'Loading processing notes...'}
            </p>
          </div>
          <button
            onClick={handleCloseModal}
            className="bg-transparent text-gray-500 hover:text-gray-800 p-2 rounded-full hover:bg-gray-100"
          >
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12"></path>
            </svg>
          </button>
        </div>

        {/* Copy notification */}
        {copied && (
          <div className="mb-4 p-3 bg-green-100 border border-green-400 text-green-700 rounded-md">
            <div className="flex items-center">
              <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7"></path>
              </svg>
              {copied}
            </div>
          </div>
        )}

        {/* Loading state */}
        {helperData === null ? (
          <div className="flex justify-center items-center py-12">
            <div className="flex flex-col items-center">
              <div className="w-8 h-8 border-4 border-ktx-purple border-t-transparent rounded-full animate-spin mb-4"></div>
              <p className="text-gray-600">Loading processing notes...</p>
            </div>
          </div>
        ) : (
          <>
            {/* Summary cards */}
            <div className="grid grid-cols-3 gap-4 mb-6">
              <div className="bg-gray-50 p-3 rounded-md">
                <div className="text-sm text-gray-500">Total Notes</div>
                <div className="text-2xl font-bold text-ktx-purple">{helperData.length}</div>
              </div>
              <div className="bg-gray-50 p-3 rounded-md">
                <div className="text-sm text-gray-500">With Jobs</div>
                <div className="text-2xl font-bold text-green-600">
                  {helperData.filter(el => el.job).length}
                </div>
              </div>
              <div className="bg-gray-50 p-3 rounded-md">
                <div className="text-sm text-gray-500">Without Jobs</div>
                <div className="text-2xl font-bold text-orange-600">
                  {helperData.filter(el => !el.job).length}
                </div>
              </div>
            </div>

            {/* Notes table */}
            <div className="overflow-x-auto">
              <table className="min-w-full bg-white">
                <thead>
                  <tr className="bg-dark-purple text-white">
                    <th className="px-4 py-2 text-left">#</th>
                    <th className="px-4 py-2 text-left">Doctor</th>
                    <th className="px-4 py-2 text-left">Email</th>
                    <th className="px-4 py-2 text-left">Patient ID</th>
                    <th className="px-4 py-2 text-left">Job Status</th>
                    <th className="px-4 py-2 text-center">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {helperData.map((el, i) => (
                    <tr key={i} className={i % 2 === 0 ? 'bg-gray-50' : 'bg-white'}>
                      <td className="border px-4 py-2 font-medium">{i + 1}</td>
                      <td className="border px-4 py-2 text-ktx-purple font-medium">
                        {el.user_name}
                      </td>
                      <td className="border px-4 py-2 text-gray-600">{el.user_email}</td>
                      <td className="border px-4 py-2">
                        <code className="bg-gray-100 px-2 py-1 rounded text-sm">
                          {el.patient_id}
                        </code>
                      </td>
                      <td className="border px-4 py-2">
                        {el.job ? (
                          <span className="bg-green-100 text-green-800 px-2 py-1 rounded-full text-sm">
                            Active: {el.job}
                          </span>
                        ) : (
                          <span className="bg-orange-100 text-orange-800 px-2 py-1 rounded-full text-sm">
                            No Job
                          </span>
                        )}
                      </td>
                      <td className="border px-4 py-2">
                        <div className="flex gap-2 justify-center">
                          <button
                            disabled={!el.job}
                            onClick={copyToClipboard}
                            value={el.job}
                            className={`px-3 py-1 rounded text-sm font-medium transition-colors ${el.job
                              ? 'bg-blue-500 text-white hover:bg-blue-600'
                              : 'bg-gray-300 text-gray-500 cursor-not-allowed'
                              }`}
                          >
                            Copy Job
                          </button>
                          <button
                            onClick={copyToClipboard}
                            value={el.patient_id}
                            className="px-3 py-1 bg-ktx-purple text-white rounded text-sm font-medium hover:bg-ktx-navy transition-colors"
                          >
                            Copy ID
                          </button>
                          <Link
                            to={`/trace?patient_id=${el.patient_id}`}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="px-3 py-1 bg-green-500 text-white rounded text-sm font-medium hover:bg-green-600 transition-colors"
                          >
                            Trace
                          </Link>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {helperData.length === 0 && (
              <div className="py-12 text-center">
                <svg className="w-16 h-16 mx-auto text-gray-400 mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                </svg>
                <h3 className="text-lg font-medium text-gray-900 mb-2">No processing notes found</h3>
                <p className="text-gray-500">There are currently no notes being processed.</p>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  )
}
